import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ScrollArea } from '@/components/ui/ScrollArea';
import {
  useListNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '@/store/api/adminApi';
import { useToastDispatch } from '@/hooks/useToastDispatch';
import { extractError } from '@/lib/extractError';

export function NotificationDropdown() {
  const toast = useToastDispatch();
  const { data, refetch } = useListNotificationsQuery({ limit: 10 });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllNotificationsReadMutation();

  const items = data?.items ?? [];
  const unreadCount = items.filter((item) => !item.read).length;

  const handleMarkRead = async (id: number) => {
    try {
      await markRead(id).unwrap();
      refetch();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead().unwrap();
      toast.success('Todas marcadas como leídas');
      refetch();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <div className="w-80 p-0">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">Notificaciones</span>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            disabled={markingAll}
            onClick={() => void handleMarkAll()}
          >
            Marcar todas
          </Button>
        )}
      </div>

      <ScrollArea className="max-h-80">
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            Sin notificaciones
          </p>
        ) : (
          <ul className="divide-y">
            {items.map((item) => (
              <li
                key={item.id}
                className={`px-3 py-3 ${item.read ? 'opacity-70' : 'bg-accent/20'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.title}</p>
                    {item.body && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {item.body}
                      </p>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {new Date(item.createdAt).toLocaleString('es-AR')}
                    </p>
                  </div>
                  {!item.read && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 shrink-0 px-2 text-xs"
                      onClick={() => void handleMarkRead(item.id)}
                    >
                      Leída
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>

      <div className="border-t p-2">
        <Link
          to="/notifications"
          className="block rounded-sm px-2 py-1.5 text-center text-sm text-primary hover:bg-accent"
        >
          Ver todas
        </Link>
      </div>
    </div>
  );
}
